/*
 *  Copyright (c) Qwoach LLC 2021. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 29/10/2021
 */

import {JournalEntryType} from "@shared/Journal/JournalEntryType";
import {JournalEntry} from "./journalEntry-imports";
import {Type, classToClass} from "@qwoach/class-transformer-wrapper";
import SessionNoteItem from "@shared/Journal/SessionNoteItem";
import SessionPlanItem from "@shared/Journal/SessionPlanItem";
import SessionPlanAnswerItem from "@shared/Journal/SessionPlanAnswerItem";
import {ensureArray} from "@/utils/helpers";
import {displayTypeFromNoteType, SessionNoteTypeEnum} from "@shared/Journal/SessionNoteTypeEnum";
import {SessionPlanItemTypeEnum} from "@shared/Journal/SessionPlanItemTypeEnum";
import {SessionPlanRollForwardEnum} from "@shared/Journal/SessionPlanRollForwardEnum";
import {getStableDefaultSessionPlanItemId} from "@shared/Journal/SessionPlanDefaultItems";

const SESSION_PLAN_ITEM_SCHEMA_VERSION = 4

export default class SessionPlanJournalEntry extends JournalEntry {

	@Type(() => SessionNoteItem)
	sessionNotes: SessionNoteItem[] = []

	@Type(() => SessionPlanItem)
	items: SessionPlanItem[] = []

	@Type(() => SessionPlanAnswerItem)
	answers: SessionPlanAnswerItem[] = []

	usesCommissionLayout: boolean = false


	constructor(options = {}) {
		super({type: JournalEntryType.SESSION_PLAN, ...options});
		this.applyOptions(options);
	}

	getCurrentSchemaVersion() {
		return SESSION_PLAN_ITEM_SCHEMA_VERSION
	}

	migrateSchema(oldVersion, newVersion) {
		super.migrateSchema(oldVersion, newVersion)
		if(oldVersion < SESSION_PLAN_ITEM_SCHEMA_VERSION){
			this.migrateLegacySessionNotesToItems()
		}
	}

	migrateLegacySessionNotesToItems(){
		if(this.sessionNotes?.length > 0){
			this.items = this.createItemsFromSessionNotes(this.sessionNotes)
			this.sessionNotes = []
			return
		}
		if(this.items?.length > 0){
			this.recalculateItemSortIndexes()
		}
	}

	createItemsFromSessionNotes(sessionNotes = []){
		let itemIndex = 0
		let items = []
		for(let noteItem of sessionNotes){
			let item = this.createItemFromSessionNote(noteItem, itemIndex)
			if(item){
				items.push(item)
				itemIndex++
			}
		}
		return items
	}

	createItemFromSessionNote(noteItem, itemIndex){
		if(!noteItem || this.shouldSkipLegacySessionNote(noteItem)){
			return null
		}
		let displayType = displayTypeFromNoteType(noteItem.noteType)
		let itemType = displayType === SessionNoteTypeEnum.GENERAL_TAGS
			? SessionPlanItemTypeEnum.TAGS
			: SessionPlanItemTypeEnum.TEXT
		return new SessionPlanItem({
			title: noteItem.title,
			description: noteItem.description,
			content: noteItem.content,
			itemType,
			sortIndex: itemIndex,
			rollForward: this.legacyRollForwardValue(noteItem),
			isReadonly: noteItem.isReadonly === true,
			defaultTagColor: noteItem.defaultTagColor,
			tags: noteItem.tags || [],
		})
	}

	shouldSkipLegacySessionNote(noteItem){
		return [
			SessionNoteTypeEnum.REF_PREV_SESSION,
			SessionNoteTypeEnum.TLSW_BOOK,
		].includes(noteItem.noteType)
	}

	legacyRollForwardValue(noteItem){
		let rollingForwardTypes = [
			SessionNoteTypeEnum.AIMS,
			SessionNoteTypeEnum.OUTCOME,
		]
		return rollingForwardTypes.includes(noteItem.noteType)
			? SessionPlanRollForwardEnum.ONCE
			: SessionPlanRollForwardEnum.NONE
	}

	setSessionMaterials(sessionMaterials){
		this.sessionMaterials = sessionMaterials;
	}

	setSessionTools(sessionTools){
		this.sessionTools = sessionTools;
	}

	getNoteItemByType(noteType){
		return this.sessionNotes.find(noteItem => noteItem.noteType === noteType)
	}

	getNotesString(noteType){
		noteType = ensureArray(noteType)
		let notes = this.sessionNotes.filter(noteItem => noteType.includes(noteItem.noteType))
		return notes.map(noteItem => noteItem.content).join("<br/>")
	}

	ensureItemsMigrated(){
		let hasLegacyNotes = this.sessionNotes?.length > 0
		let isLegacySchema = !this.schemaVersion || this.schemaVersion < SESSION_PLAN_ITEM_SCHEMA_VERSION
		let needsFallbackMigration = (!this.items || this.items.length === 0) && hasLegacyNotes
		if((isLegacySchema && hasLegacyNotes) || needsFallbackMigration){
			this.migrateLegacySessionNotesToItems()
			this.schemaVersion = SESSION_PLAN_ITEM_SCHEMA_VERSION
		}
	}

	getItemArray(){
		this.ensureItemsMigrated()
		this.assignStableDefaultItemIds()
		return [...(this.items || [])].sort((left, right) => (left.sortIndex || 0) - (right.sortIndex || 0))
	}

	getItemsForLayout(layoutItems = []) {
		if(!this.usesCommissionLayout){
			return this.getItemArray()
		}
		let answersById = new Map((this.answers || []).map(answer => [answer.id, answer]))
		return layoutItems.map(layoutItem => {
			let answer = answersById.get(layoutItem.id)
			return new SessionPlanItem({
				...layoutItem, content: answer?.content || null, tags: answer?.tags || [],
			})
		})
	}

	setAnswer(item) {
		let answers = this.answers || []
		let existing = answers.find(answer => answer.id === item.id)
		if(existing){
			existing.content = item.content
			existing.tags = item.tags || []
		} else {
			answers.push(new SessionPlanAnswerItem({id: item.id, content: item.content, tags: item.tags || []}))
		}
		this.answers = answers
	}

	migrateToCommissionLayout() {
		if(this.usesCommissionLayout) return false
		for(let item of this.getItemArray()){
			if(item.hasContent?.()) this.setAnswer(item)
		}
		this.items = []
		this.sessionNotes = []
		this.usesCommissionLayout = true
		return true
	}

	assignStableDefaultItemIds(){
		for(let item of this.items || []){
			let stableId = getStableDefaultSessionPlanItemId(this.type, item.title)
			if(stableId && !this.items.some(localItem => localItem !== item && localItem.id === stableId)){
				item.id = stableId
			}
		}
	}

	getLayoutItemArray(){
		return this.getItemArray().map(item => this.createLayoutItemFromItem(item))
	}

	createLayoutItemFromItem(item){
		let layoutItem = classToClass(item)
		layoutItem.content = null
		layoutItem.tags = []
		layoutItem.isReadonly = false
		return layoutItem
	}

	applyLayoutItems(layoutItems = []){
		let existingItems = this.getItemArray()
		let existingItemsById = existingItems.reduce((map, item) => {
			map[item.id] = item
			return map
		}, {})
		let nextItems = layoutItems.map(layoutItem => {
			let nextItem = classToClass(layoutItem)
			let existingItem = existingItemsById[nextItem.id]
			if(existingItem){
				nextItem.content = existingItem.content
				nextItem.tags = existingItem.tags || []
			}
			return nextItem
		})
		let layoutItemIds = layoutItems.map(item => item.id)
		let removedItemsWithContent = existingItems.filter(item => {
			return !layoutItemIds.includes(item.id) && item.hasContent?.()
		})
		nextItems.push(...removedItemsWithContent)
		this.items = nextItems
		this.recalculateItemSortIndexes()
		return this
	}

	getRollForwardItemArray(){
		return this.getItemArray().filter(item => [SessionPlanRollForwardEnum.ONCE, SessionPlanRollForwardEnum.ALL].includes(item.rollForward))
	}

	getItemById(itemId){
		return (this.items || []).find(item => item.id === itemId)
	}

	updateItem(item){
		let existingItem = this.getItemById(item?.id)
		if(existingItem){
			Object.assign(existingItem, item)
		}
		return existingItem
	}

	recalculateItemSortIndexes(){
		this.items = this.items || []
		this.items.forEach((item, index) => {
			item.sortIndex = index
		})
	}

	getSessionItemsExecuted(){
		return this.getItemArray()
	}

	getSessionNotesExecuted(){
		return this.getSessionItemsExecuted()
	}

}

export function getCarriedForwardSessionPlanItems(priorSessions = [], layoutItems = [], allSessionsAreShared = false) {
	let carriedItemsById = new Map()
	let lastSessionIndex = priorSessions.length - 1
	for(let [index, session] of priorSessions.entries()){
		let plan = session.getEntryByType?.([JournalEntryType.TLSW_SESSION_PLAN, JournalEntryType.SESSION_PLAN])
		for(let item of plan?.getItemsForLayout?.(layoutItems) || plan?.getItemArray?.() || []){
			let carriesToCurrent = (!allSessionsAreShared && item.rollForward === SessionPlanRollForwardEnum.ALL)
				|| (index === lastSessionIndex && item.rollForward === SessionPlanRollForwardEnum.ONCE)
			if(carriesToCurrent && item.hasContent?.()){
				carriedItemsById.set(item.id, item)
			}
		}
	}
	return [...carriedItemsById.values()]
}
